import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BookCover from "@/components/BookCover";
import { Seo } from "@/components/Seo";

function bookSchema(book) {
  const schema = {
    "@context": "https://schema.org",
    "@type": "Book",
    name: book.title,
    author: { "@type": "Person", name: book.author },
    publisher: { "@type": "Organization", name: "1123 Publishing Company" },
    description: book.description,
    url: `https://1123publishing.com/book/${book.slug}`,
  };
  if (book.cover_image) schema.image = book.cover_image;
  if (book.price && !book.coming_soon) {
    schema.offers = {
      "@type": "Offer",
      price: String(book.price),
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
      url: `https://1123publishing.com/book/${book.slug}`,
    };
  }
  return schema;
}

export default function BookDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);

  useEffect(() => {
    api.get(`/books/${slug}`)
      .then((r) => setBook(r.data))
      .catch(() => navigate("/bookstore"));
  }, [slug, navigate]);

  if (!book) return <div className="py-32 text-center text-navy/60">Loading&hellip;</div>;

  const comingSoon = !!book.coming_soon;
  const hasAmazon = !!book.amazon_url;
  const hasEditions = Array.isArray(book.editions) && book.editions.length > 0;
  const useAmazonFlow = !comingSoon && (hasAmazon || hasEditions);

  return (
    <div className="py-16 md:py-24 bg-white" data-testid="book-detail-page">
      <Seo
        title={`${book.title} by ${book.author} | 1123 Publishing Company`}
        description={book.description}
        path={`/book/${book.slug}`}
        image={book.cover_image}
        type="book"
        jsonLd={bookSchema(book)}
      />
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <Link to="/bookstore" className="text-sm text-navy/60 hover:text-gold uppercase tracking-[0.2em]">
          &larr; The Catalog
        </Link>

        <div className="grid md:grid-cols-2 gap-12 lg:gap-20 mt-8">
          <div className="flex justify-center md:justify-end">
            <BookCover book={book} size="lg" />
          </div>

          <div>
            <BookHeader book={book} comingSoon={comingSoon} />
            <BookMeta book={book} comingSoon={comingSoon} />

            {comingSoon ? (
              <WaitlistBlock book={book} />
            ) : useAmazonFlow ? (
              <AmazonBuyBlock book={book} hasEditions={hasEditions} />
            ) : (
              <StripeCheckoutForm book={book} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function BookHeader({ book, comingSoon }) {
  return (
    <>
      <div className="overline">{comingSoon ? "Forthcoming · 1123 Imprint" : book.category}</div>
      <h1 className="font-serif text-4xl md:text-5xl text-navy mt-2 leading-tight">{book.title}</h1>
      <div className="mt-3 text-navy/70 font-serif text-xl italic">by {book.author}</div>
      <div className="ornament-rule my-7 max-w-xs"><span>&#10086;</span></div>
      <p className="text-charcoal/85 leading-relaxed">{book.description}</p>
    </>
  );
}

function BookMeta({ book, comingSoon }) {
  return (
    <div className="grid grid-cols-3 gap-4 mt-8 text-sm">
      {book.pages ? (
        <div>
          <div className="overline">Pages</div>
          <div className="font-serif text-xl text-navy mt-1">{book.pages}</div>
        </div>
      ) : null}
      {book.published_year && (
        <div>
          <div className="overline">{comingSoon ? "Releases" : "Year"}</div>
          <div className="font-serif text-xl text-navy mt-1">{book.published_year}</div>
        </div>
      )}
      <div>
        <div className="overline">Price</div>
        <div className="font-serif text-xl text-gold mt-1">${Number(book.price).toFixed(2)}</div>
      </div>
    </div>
  );
}

function WaitlistBlock({ book }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/waitlist", { email, name, book_slug: book.slug });
      setWaitlisted(true);
      toast.success(`You're on the list. We'll email you when ${book.title} is available.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not join the waitlist.");
    } finally {
      setSubmitting(false);
    }
  };

  if (waitlisted) {
    return (
      <div className="mt-10 border border-gold/40 bg-parchment p-8" data-testid="waitlist-confirmation">
        <div className="overline mb-2">On the List</div>
        <h3 className="font-serif text-2xl text-navy">We'll be in touch.</h3>
        <p className="text-charcoal/80 mt-3 text-sm leading-relaxed">
          The moment <em>{book.title}</em> is available, we'll send an email to <strong>{email}</strong>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={joinWaitlist} className="mt-10 space-y-4 border-t border-navy/10 pt-8" data-testid="waitlist-form">
      <div className="border border-gold/40 bg-parchment p-5 mb-4">
        <div className="overline text-gold">Coming Soon</div>
        <p className="text-charcoal/80 mt-2 text-sm leading-relaxed">
          Join the waitlist and we'll let you know the moment <strong>{book.title}</strong> is available for purchase.
        </p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="overline">Your Name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)}
            className="w-full mt-2 bg-parchment border border-navy/15 px-4 py-3 focus:border-gold focus:outline-none"
            data-testid="waitlist-name" />
        </div>
        <div>
          <label className="overline">Email</label>
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-2 bg-parchment border border-navy/15 px-4 py-3 focus:border-gold focus:outline-none"
            data-testid="waitlist-email" />
        </div>
      </div>
      <button type="submit" disabled={submitting} data-testid="waitlist-submit"
        className="w-full bg-navy text-ivory py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-charcoal disabled:opacity-60 transition-colors">
        {submitting ? "Adding you to the list..." : "Join the Waitlist"}
      </button>
    </form>
  );
}

function AmazonBuyBlock({ book, hasEditions }) {
  return (
    <div className="mt-10 border-t border-navy/10 pt-8 space-y-4" data-testid="amazon-buy">
      <div className="overline mb-2 text-gold">Available Now</div>
      {hasEditions ? <EditionsGrid editions={book.editions} /> : <SingleAmazonLink url={book.amazon_url} />}
      <p className="text-xs text-navy/55 text-center">
        You'll be directed to Amazon to complete your purchase. We use Amazon as a trusted vendor while our direct checkout is being prepared.
      </p>
    </div>
  );
}

function EditionsGrid({ editions }) {
  return (
    <>
      <p className="text-sm text-charcoal/75 mb-4">
        Choose your edition. Each links to the title on Amazon, where you can purchase paperback copies for immediate fulfillment.
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {editions.map((ed) => (
          <a key={ed.url} href={ed.url} target="_blank" rel="noopener noreferrer"
            data-testid={`amazon-edition-${ed.label.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
            className="group border border-navy/20 bg-white p-4 hover:border-gold transition-colors flex items-center justify-between gap-3">
            <span className="font-serif text-navy text-base leading-tight group-hover:text-gold transition-colors">{ed.label}</span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60 whitespace-nowrap">Amazon ↗</span>
          </a>
        ))}
      </div>
    </>
  );
}

function SingleAmazonLink({ url }) {
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" data-testid="amazon-buy-btn"
      className="block w-full bg-[#FF9900] text-navy text-center py-4 text-[12px] uppercase tracking-[0.22em] font-semibold hover:bg-gold transition-colors">
      Buy on Amazon ↗
    </a>
  );
}

function StripeCheckoutForm({ book }) {
  const [qty, setQty] = useState(1);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const buy = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/payments/checkout/book", {
        book_id: book.id,
        quantity: qty,
        customer_email: email,
        customer_name: name,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed.");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={buy} className="mt-10 space-y-4 border-t border-navy/10 pt-8" data-testid="book-checkout-form">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="overline">Your Name</label>
          <input required value={name} onChange={(e) => setName(e.target.value)}
            className="w-full mt-2 bg-parchment border border-navy/15 px-4 py-3 focus:border-gold focus:outline-none"
            data-testid="checkout-name" />
        </div>
        <div>
          <label className="overline">Email</label>
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full mt-2 bg-parchment border border-navy/15 px-4 py-3 focus:border-gold focus:outline-none"
            data-testid="checkout-email" />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <label className="overline">Quantity</label>
        <div className="flex border border-navy/15">
          <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 text-navy hover:bg-parchment">&minus;</button>
          <div className="px-5 py-2 font-serif text-lg text-navy min-w-[3rem] text-center" data-testid="checkout-qty">{qty}</div>
          <button type="button" onClick={() => setQty(Math.min(50, qty + 1))} className="px-3 py-2 text-navy hover:bg-parchment">+</button>
        </div>
        <div className="ml-auto font-serif text-2xl text-navy">${(book.price * qty).toFixed(2)}</div>
      </div>
      <button type="submit" disabled={submitting} data-testid="checkout-submit"
        className="w-full bg-navy text-ivory py-4 text-[11px] uppercase tracking-[0.24em] hover:bg-charcoal disabled:opacity-60 transition-colors">
        {submitting ? "Redirecting to checkout..." : "Purchase via Stripe"}
      </button>
      <p className="text-xs text-navy/50 text-center">Secure checkout powered by Stripe. Test card 4242 4242 4242 4242.</p>
    </form>
  );
}
